// Generic table driven by a column config, so views don't repeat <table> markup.
// columns: [{ key, label, render?(row), className?, primary?, action?, sortable?, sortValue?(row) }].
//   primary  — on phones this cell is the card's headline (no label shown).
//   action   — on phones this cell holds full-width action buttons.
// onRowClick is optional. Set `scroll` for dense admin tables that should stay a
// table and scroll horizontally on phones instead of collapsing into cards.
//
// Sorting is OPT-IN and lives with the caller: a screen passes `sort` ({ key, dir })
// and `onSort(key)` — both from useTableSort, which owns the comparator — and the head
// becomes clickable. A table that passes neither renders exactly as it always has, so
// every existing caller is untouched. Columns opt out with `sortable: false`, and an
// action column never sorts (there is nothing to order by).
//
// On phones (<= 560px) responsive.css restyles the same markup into stacked
// cards using each cell's data-label + col-primary / col-action classes, so
// there is no second render path — every screen gets cards for free.
import he from '../i18n/he';
import EmptyState from './EmptyState';

const cellClass = (c) =>
  [c.className, c.primary ? 'col-primary' : '', c.action ? 'col-action' : '']
    .filter(Boolean)
    .join(' ') || undefined;

export default function DataTable({ columns, rows, onRowClick, rowClass, empty, scroll, sort, onSort }) {
  if (!rows || rows.length === 0) return <EmptyState {...(empty || {})} />;
  return (
    <div className={`table-wrap${scroll ? ' scroll' : ''}`}>
      <table className="table">
        <thead>
          <tr>
            {columns.map((c) => {
              const sortable = !!onSort && c.sortable !== false && !c.action;
              const active = sortable && sort && sort.key === c.key;
              return (
                <th
                  key={c.key}
                  className={[c.className, sortable ? 'sortable' : ''].filter(Boolean).join(' ') || undefined}
                  onClick={sortable ? () => onSort(c.key) : undefined}
                  aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  {c.label}
                  {/* The sorted column states its direction; every other sortable one
                      carries a faint two-way arrow that shows on hover, so it is
                      discoverable that a header can be clicked at all. */}
                  {sortable && (
                    <span className={`sort-arrow${active ? ' on' : ''}`}>
                      {active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
                    </span>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={row.id ?? i}
              className={[onRowClick ? 'clickable' : '', rowClass ? rowClass(row) : ''].filter(Boolean).join(' ')}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {columns.map((c) => (
                <td key={c.key} className={cellClass(c)} data-label={c.label}>
                  {/* Wrapper keeps a multi-node cell grouped as one value on the
                      card layout, opposite its label. */}
                  <span className="cell-value">{c.render ? c.render(row) : row[c.key]}</span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {/* A total row, when a column brings one (`footer`). The figures are the
            caller's — the server's sums — never added up here. */}
        {columns.some((c) => c.footer != null) && (
          <tfoot>
            <tr>
              {columns.map((c) => (
                <td key={c.key} className={cellClass(c)} data-label={c.label}>
                  <span className="cell-value">{c.footer}</span>
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

export const emptyLabel = he.common.empty;
